import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Bot } from "lucide-react";
import { Head } from 'vite-react-ssg';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import NavigationButtons from '@/components/NavigationButtons';
import { WaitlistDialog } from '@/components/WaitlistDialog';
import RelatedLinks from '@/components/RelatedLinks';
import InContentCTA from '@/components/InContentCTA';
import { supabase } from '@/integrations/supabase/client';
import { getPrebuiltPost } from '@/lib/db-posts';

import { trackEvent, EXTENSION_URL } from '@/lib/analytics';
import DOMPurify from 'dompurify';

// DOMPurify needs a DOM, which the prerender step (Node) doesn't have, and its
// output can differ from the raw markup — sanitizing during the first client
// render would break hydration. So the first paint mirrors the server exactly
// and sanitizing kicks in right after mount.
const sanitize = (html: string, active: boolean) =>
  !active || typeof window === 'undefined' || typeof DOMPurify.sanitize !== 'function'
    ? html
    : DOMPurify.sanitize(html);



/**
 * Split HTML content at the closest </p> boundary past the half-way mark so
 * we can inject an in-content CTA mid-article. Falls back to appending if the
 * article is too short to split cleanly.
 */
function splitContentForCTA(html: string): { before: string; after: string } {
  if (!html) return { before: '', after: '' };
  const closeTags = [...html.matchAll(/<\/p>/gi)].map((m) => (m.index ?? 0) + m[0].length);
  if (closeTags.length < 4) return { before: html, after: '' };
  const target = html.length / 2;
  const splitAt = closeTags.find((idx) => idx >= target) ?? closeTags[Math.floor(closeTags.length / 2)];
  return { before: html.slice(0, splitAt), after: html.slice(splitAt) };
}

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Flips to true after mount so DOMPurify runs post-hydration only.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);


  // Build-time snapshot of the auto-generated posts. Present during prerender,
  // so the static HTML ships the real title, meta and body instead of a shell.
  const prebuilt = getPrebuiltPost(slug);

  // Try to fetch from database (keeps edits/new posts fresh after the build)
  const { data: livePost, isLoading: isLiveLoading } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug!)
        .eq('status', 'published')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const dbPost = (livePost ?? prebuilt) as typeof livePost | undefined;
  const isLoading = isLiveLoading && !prebuilt;


  // Fallback static post
  const fallbackPost = {
    title: "Deep Seek AI Launches Revolutionary Language Model",
    date: "February 7, 2025",
    author: "Deep Seek Team",
    content: `Deep Seek AI is proud to announce the launch of our latest language model, setting new standards in natural language processing and understanding.`,
    meta_description: "Explore Deep Seek's revolutionary language model with enhanced contextual understanding and improved reasoning capabilities.",
  };

  const post = dbPost
    ? {
        title: dbPost.title,
        date: new Date(dbPost.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        author: "Deep Seek AI",
        content: dbPost.content,
        meta_description: dbPost.meta_description || dbPost.excerpt,
      }
    : fallbackPost;

  const isDynamic = !!dbPost;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": post.title.length > 110 ? post.title.slice(0, 107) + '…' : post.title,
    "image": dbPost?.hero_image_url ? [dbPost.hero_image_url] : ["https://deepseek.ai/og-image.png"],
    "datePublished": dbPost?.published_at || "2025-02-07T08:00:00+00:00",
    "dateModified": dbPost?.updated_at || dbPost?.published_at || "2025-02-07T08:00:00+00:00",
    "author": [{ "@type": "Person", "name": "Frank", "url": "https://deepseek.ai" }],
    "publisher": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide",
      "logo": { "@type": "ImageObject", "url": "https://deepseek.ai/logo.png" }
    },
    "description": post.meta_description,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://deepseek.ai/blog/${slug}` }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading article...</div>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>{post.title.length > 60 ? post.title.slice(0, 57) + '…' : post.title}</title>
        <meta name="description" content={post.meta_description} />
        <link rel="canonical" href={`https://deepseek.ai/blog/${slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://deepseek.ai/blog/${slug}`} />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.meta_description} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
          <Button variant="ghost" onClick={() => navigate('/blog')} className="mb-8 hover:bg-gray-100">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>

          

          <article>
            <div className="mb-8">
              <div className="text-gray-500 mb-2">
                {post.date} • {post.author}
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
            </div>

            {isDynamic ? (
              (() => {
                const { before, after } = splitContentForCTA(post.content);
                return (
                  <div className="mb-12">
                    <div
                      className="prose prose-lg max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitize(before, hydrated) }}
                    />
                    <InContentCTA placement="blog_mid" />
                    {after && (
                      <div
                        className="prose prose-lg max-w-none"
                        dangerouslySetInnerHTML={{ __html: sanitize(after, hydrated) }}
                      />
                    )}
                  </div>
                );
              })()
            ) : (
              <div className="mb-12">
                <div className="prose prose-lg max-w-none">
                  <p>{post.content}</p>
                </div>
                <InContentCTA placement="blog_mid_static" />
              </div>
            )}

            <div className="mt-12 mb-12 border-t border-b border-gray-200 py-8">
              <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
              <RelatedLinks />
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
              <a
                href={EXTENSION_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('blog_to_extension', { placement: 'blog_footer' })}
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-[#0066FF] hover:bg-[#0066FF]/90 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
              >
                Try Deep Seek AI Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </article>
        </div>
      </div>

      <WaitlistDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
    </>
  );
};

export default BlogPost;
